import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, CreditCard, RefreshCw, Calendar, Shield, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const Policies = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Booking & Cancellation Policies',
      subtitle: 'Transparent policies for peace of mind',
      backToHome: 'Back to Home',
      
      paymentTitle: 'Payment Policy',
      paymentIntro: 'We offer flexible payment options to make booking your adventure easy:',
      paymentItems: [
        {
          title: 'Deposit to Book',
          description: 'Secure your spot with a 5% deposit of your total trip cost. This deposit is non-refundable but guarantees your place on the adventure.'
        },
        {
          title: 'Balance Due',
          description: 'The remaining balance (95%) is due 45 days before your trip departure date. We\'ll send you friendly reminders at 14 days and 7 days before the due date.'
        },
        {
          title: 'Pay in Full',
          description: 'Prefer to pay everything upfront? You can pay the full amount at the time of booking and skip worrying about the balance later.'
        },
        {
          title: 'Accepted Payment Methods',
          description: 'We accept all major credit cards (Visa, Mastercard, American Express) through our secure Stripe payment system.'
        }
      ],
      
      refundTitle: 'Cancellation & Refund Policy',
      refundIntro: 'We understand plans can change. Our cancellation policy is designed to be fair while allowing us to maintain our commitment to our partners and destinations:',
      refundTiers: [
        {
          timeframe: '45+ days before trip',
          refund: '95% refund',
          description: 'Full refund minus the 5% non-refundable deposit',
          color: 'green'
        },
        {
          timeframe: '30-45 days before trip',
          refund: '45% refund',
          description: 'Partial refund to cover committed costs',
          color: 'yellow'
        },
        {
          timeframe: 'Less than 30 days',
          refund: 'No refund',
          description: 'Full amount retained due to non-recoverable commitments',
          color: 'red'
        }
      ],
      
      nonRefundableTitle: 'Non-Refundable Deposit',
      nonRefundableText: 'The 5% booking deposit is non-refundable regardless of cancellation timing. This covers administrative costs and secures your spot.',
      
      howToCancel: 'How to Cancel',
      howToCancelText: 'To cancel your booking, please contact us at hello@exciting.travel or call (833) 568-8090. Refunds are processed within 5-10 business days to your original payment method.',
      
      travelInsurance: 'Travel Insurance',
      travelInsuranceText: 'We strongly recommend purchasing travel insurance to protect your investment. Travel insurance can cover cancellations due to illness, emergencies, and other unforeseen circumstances that may not be covered by our standard policy.',
      
      questionsTitle: 'Questions?',
      questionsText: 'If you have any questions about our policies, please don\'t hesitate to reach out. We\'re here to help make your travel experience worry-free.',
      contactUs: 'Contact Us'
    },
    es: {
      title: 'Políticas de Reserva y Cancelación',
      subtitle: 'Políticas transparentes para tu tranquilidad',
      backToHome: 'Volver al Inicio',
      
      paymentTitle: 'Política de Pago',
      paymentIntro: 'Ofrecemos opciones de pago flexibles para facilitar la reserva de tu aventura:',
      paymentItems: [
        {
          title: 'Depósito para Reservar',
          description: 'Asegura tu lugar con un depósito del 5% del costo total del viaje. Este depósito no es reembolsable pero garantiza tu lugar en la aventura.'
        },
        {
          title: 'Saldo Pendiente',
          description: 'El saldo restante (95%) vence 45 días antes de la fecha de salida. Te enviaremos recordatorios amigables a los 14 días y 7 días antes de la fecha de vencimiento.'
        },
        {
          title: 'Pago Completo',
          description: '¿Prefieres pagar todo por adelantado? Puedes pagar el monto total al momento de reservar y olvidarte del saldo.'
        },
        {
          title: 'Métodos de Pago Aceptados',
          description: 'Aceptamos todas las tarjetas de crédito principales (Visa, Mastercard, American Express) a través de nuestro sistema de pago seguro Stripe.'
        }
      ],
      
      refundTitle: 'Política de Cancelación y Reembolso',
      refundIntro: 'Entendemos que los planes pueden cambiar. Nuestra política de cancelación está diseñada para ser justa mientras mantenemos nuestro compromiso con nuestros socios y destinos:',
      refundTiers: [
        {
          timeframe: 'Más de 45 días antes del viaje',
          refund: '95% de reembolso',
          description: 'Reembolso completo menos el depósito no reembolsable del 5%',
          color: 'green'
        },
        {
          timeframe: '30-45 días antes del viaje',
          refund: '45% de reembolso',
          description: 'Reembolso parcial para cubrir costos comprometidos',
          color: 'yellow'
        },
        {
          timeframe: 'Menos de 30 días',
          refund: 'Sin reembolso',
          description: 'Monto total retenido debido a compromisos no recuperables',
          color: 'red'
        }
      ],
      
      nonRefundableTitle: 'Depósito No Reembolsable',
      nonRefundableText: 'El depósito de reserva del 5% no es reembolsable independientemente del momento de cancelación. Esto cubre costos administrativos y asegura tu lugar.',
      
      howToCancel: 'Cómo Cancelar',
      howToCancelText: 'Para cancelar tu reserva, contáctanos en hello@exciting.travel o llama al (833) 568-8090. Los reembolsos se procesan en 5-10 días hábiles a tu método de pago original.',
      
      travelInsurance: 'Seguro de Viaje',
      travelInsuranceText: 'Recomendamos encarecidamente comprar un seguro de viaje para proteger tu inversión. El seguro de viaje puede cubrir cancelaciones por enfermedad, emergencias y otras circunstancias imprevistas que no están cubiertas por nuestra política estándar.',
      
      questionsTitle: '¿Preguntas?',
      questionsText: 'Si tienes alguna pregunta sobre nuestras políticas, no dudes en contactarnos. Estamos aquí para hacer tu experiencia de viaje sin preocupaciones.',
      contactUs: 'Contáctanos'
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
      {/* Header */}
      <div className="bg-[#84bc00]/10 border-b border-[#84bc00]/30">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-[#84bc00] hover:text-white transition-colors mb-6"
          >
            <ChevronLeft size={20} />
            {t.backToHome}
          </Link>
          <h1 className="text-4xl md:text-5xl font-black mb-4">{t.title}</h1>
          <p className="text-xl text-gray-300">{t.subtitle}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12 space-y-16">
        {/* Payment Policy */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#84bc00] flex items-center justify-center">
              <CreditCard size={24} className="text-white" />
            </div>
            <h2 className="text-3xl font-black">{t.paymentTitle}</h2>
          </div>
          
          <p className="text-lg text-gray-300 mb-8">{t.paymentIntro}</p>
          
          <div className="grid gap-6">
            {t.paymentItems.map((item, index) => (
              <div 
                key={index}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#84bc00]/50 transition-colors"
              >
                <h3 className="text-xl font-bold text-[#84bc00] mb-2">{item.title}</h3>
                <p className="text-gray-300">{item.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Refund Policy */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#84bc00] flex items-center justify-center">
              <RefreshCw size={24} className="text-white" />
            </div>
            <h2 className="text-3xl font-black">{t.refundTitle}</h2>
          </div>
          
          <p className="text-lg text-gray-300 mb-8">{t.refundIntro}</p>
          
          {/* Refund Tiers */}
          <div className="grid gap-4 mb-8">
            {t.refundTiers.map((tier, index) => (
              <div 
                key={index}
                className={`rounded-xl p-6 border-2 ${
                  tier.color === 'green' ? 'bg-green-500/10 border-green-500/50' :
                  tier.color === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/50' :
                  'bg-red-500/10 border-red-500/50'
                }`}
              >
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar size={18} className={
                        tier.color === 'green' ? 'text-green-400' :
                        tier.color === 'yellow' ? 'text-yellow-400' :
                        'text-red-400'
                      } />
                      <span className="font-bold text-lg">{tier.timeframe}</span>
                    </div>
                    <p className="text-gray-400 text-sm">{tier.description}</p>
                  </div>
                  <div className={`text-2xl font-black ${
                    tier.color === 'green' ? 'text-green-400' :
                    tier.color === 'yellow' ? 'text-yellow-400' :
                    'text-red-400'
                  }`}>
                    {tier.refund}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Non-refundable Note */}
          <div className="bg-[#84bc00]/10 border border-[#84bc00]/30 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertCircle className="text-[#84bc00] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-bold text-lg mb-2">{t.nonRefundableTitle}</h3>
                <p className="text-gray-300">{t.nonRefundableText}</p>
              </div>
            </div>
          </div>

          {/* How to Cancel */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <h3 className="text-xl font-bold mb-2">{t.howToCancel}</h3>
            <p className="text-gray-300">{t.howToCancelText}</p>
          </div>
        </section>

        {/* Travel Insurance */}
        <section>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#84bc00] flex items-center justify-center">
              <Shield size={24} className="text-white" />
            </div>
            <h2 className="text-3xl font-black">{t.travelInsurance}</h2>
          </div>
          
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
            <p className="text-gray-300">{t.travelInsuranceText}</p>
          </div>
        </section>

        {/* Questions */}
        <section className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4">{t.questionsTitle}</h2>
          <p className="text-gray-300 mb-6 max-w-xl mx-auto">{t.questionsText}</p>
          <Link 
            to="/consultation"
            className="inline-block bg-[#84bc00] hover:bg-[#6a9600] text-white font-bold px-8 py-4 rounded-lg transition-colors"
          >
            {t.contactUs}
          </Link>
        </section>
      </div>
    </div>
  );
};

export default Policies;
